import { Component, type ErrorInfo, type ReactNode } from 'react';
import { ErrorPage } from './error-page';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Custom fallback UI to show on error */
  fallback?: ReactNode;
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React Error Boundary to prevent white screen crashes.
 *
 * Features:
 * - Catches JavaScript errors in child component tree
 * - Displays user-friendly error page
 * - Logs errors to monitoring service
 * - Provides "Try Again" action
 *
 * @see Spec 0012 Section 7.4 - Error Boundary (OPS-01 - CRITICAL)
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    if (import.meta.env.DEV) console.error('ErrorBoundary caught:', error, errorInfo);

    // Delegate to monitoring callback if provided
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      return (
        this.props.fallback ?? (
          <ErrorPage
            title="Etwas ist schiefgelaufen"
            message="Ein unerwarteter Fehler ist aufgetreten. Bitte versuchen Sie es erneut."
            onRetry={this.handleRetry}
            showHomeLink
          />
        )
      );
    }
    return this.props.children;
  }
}
